import { FeedbackDialog, FiscalNoteForm, PageHeader } from '@/components'
import { FiscalNoteFormData } from '@/components/fiscal-note-form/schema'
import { FiscalNoteFormRef } from '@/components/fiscal-note-form/fiscal-note-form'
import { routes } from '@/router'
import { Typography } from '@mui/material'
import { ConfirmationDialog } from './confirmation-dialog'
import { useState, useRef, useEffect } from 'react'
import {
  invalidateFiscalNoteListQueries,
  readFiscalNoteList,
  useIssueFiscalNote,
} from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { FiscalNote } from '@/types'
import { DuplicatedFiscalNoteAlertDialog } from './duplicated-fiscal-note-alert-dialog'
import { formatISO, subDays } from 'date-fns'

export function FiscalNoteIssuePage() {
  const { company } = useCompany()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const locationState = useLocation().state as {
    fiscalNote?: FiscalNote
  } | null

  const formRef = useRef<FiscalNoteFormRef>(null)

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false)

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
  const [isDuplicatedAlertDialogOpen, setIsDuplicatedAlertDialogOpen] =
    useState(false)
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false)

  const [fiscalNoteData, setFiscalNoteData] = useState<FiscalNoteFormData>()

  const {
    mutate: issueFiscalNote,
    isPending: issuingFiscalNote,
    error: fiscalNoteError,
  } = useIssueFiscalNote({
    onSuccess: () => {
      invalidateFiscalNoteListQueries(queryClient)
      setIsSuccessDialogOpen(true)
    },
    onError: () => setIsErrorDialogOpen(true),
    onSettled: () => setIsConfirmationDialogOpen(false),
  })

  const handleSubmitForm = async (data: FiscalNoteFormData) => {
    setFiscalNoteData(data)

    try {
      setIsCheckingDuplicate(true)

      const { data: notes } = await queryClient.fetchQuery({
        queryKey: ['fiscal-note-duplicate-check', company.key],
        queryFn: () => fetchRecentFiscalNotes(company.key),
      })

      if (isFiscalNoteDuplicated(notes, data)) {
        setIsDuplicatedAlertDialogOpen(true)
        return
      }

      setIsConfirmationDialogOpen(true)
    } catch (error) {
      console.log(error)
    } finally {
      setIsCheckingDuplicate(false)
    }
  }

  const handleSubmitFiscalNote = () => {
    const {
      borrower,
      value,
      description,
      service_code_id,
      cnae_id,
      service_tax_rate,
      service_tax_code,
      csll,
      irrf,
      pis,
      cofins,
      iss_retention,
    } = fiscalNoteData!

    issueFiscalNote({
      companyKey: company.key,
      fiscalNoteData: {
        borrower_key: borrower.key,
        value: Number(value),
        description,
        service_code_id: Number(service_code_id),
        cnae_id: Number(cnae_id),
        service_tax_rate: Number(service_tax_rate) / 100,
        service_tax_code,
        csll: csll ? Number(csll) : undefined,
        irrf: irrf ? Number(irrf) : undefined,
        pis: pis ? Number(pis) : undefined,
        cofins: cofins ? Number(cofins) : undefined,
        iss_retention,
      },
    })
  }

  const handleReturnToFiscalNotes = () => {
    invalidateFiscalNoteListQueries(queryClient)
    navigate(routes.fiscalNotes.index)
  }

  const handleIssueAnotherFiscalNote = () => {
    setIsSuccessDialogOpen(false)
    formRef.current?.reset()
  }

  useEffect(() => {
    if (!locationState?.fiscalNote) return
    formRef.current?.setFiscalNote(locationState.fiscalNote)
  }, [locationState?.fiscalNote])

  return (
    <>
      <PageHeader rollbackRoute={routes.fiscalNotes.index}>
        Notas Fiscais
      </PageHeader>

      <FiscalNoteForm
        ref={formRef}
        onSubmit={handleSubmitForm}
        loading={isCheckingDuplicate}
      />

      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        onClose={() => setIsConfirmationDialogOpen(false)}
        onConfirm={handleSubmitFiscalNote}
        fiscalNoteData={fiscalNoteData}
        loading={issuingFiscalNote}
      />

      <DuplicatedFiscalNoteAlertDialog
        open={isDuplicatedAlertDialogOpen}
        onClose={() => setIsDuplicatedAlertDialogOpen(false)}
        onConfirm={() => {
          setIsDuplicatedAlertDialogOpen(false)
          setIsConfirmationDialogOpen(true)
        }}
      />

      {isSuccessDialogOpen && (
        <FeedbackDialog
          open={isSuccessDialogOpen}
          type="success"
          title="Nota fiscal enviada para a prefeitura!"
          closeLabel="Voltar"
          confirmLabel="Emitir outra nota"
          onClose={handleReturnToFiscalNotes}
          onConfirm={handleIssueAnotherFiscalNote}
        >
          <Typography textAlign="center">
            Agora é só aguardar. A emissão pode demorar de 30 segundos a 15
            minutos dependendo do município.
          </Typography>
        </FeedbackDialog>
      )}

      {isErrorDialogOpen && (
        <FeedbackDialog
          open={isErrorDialogOpen}
          type="error"
          title="Erro ao enviar nota fiscal para a prefeitura!"
          closeLabel="Voltar e corrigir nota"
          onClose={() => setIsErrorDialogOpen(false)}
        >
          <Typography textAlign="center">{fiscalNoteError?.message}</Typography>
        </FeedbackDialog>
      )}
    </>
  )
}

const fetchRecentFiscalNotes = (companyKey: string) =>
  readFiscalNoteList({
    company_key: companyKey,
    start_date: formatISO(subDays(new Date(), 30), {
      representation: 'date',
    }),
    end_date: formatISO(new Date(), { representation: 'date' }),
    page: 1,
    page_size: 200,
  })

function isFiscalNoteDuplicated(
  notes: FiscalNote[],
  data: FiscalNoteFormData,
): boolean {
  const value = Number(data.value)

  return notes.some(
    (note) =>
      note.borrower?.key === data.borrower.key && Number(note.value) === value,
  )
}
